import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { STATES } from "@/consts/states";
import { cn } from "@/lib/utils";
import type { CredentialFormField as FieldDescriptor } from "@api/shared-runtime/credential-form-descriptors";

/** How a value that is not on record yet reads: the field itself, not a note
 * beside it, so the change is visible at a glance down a long form. */
const CHANGED_RING = "ring-2 ring-amber-300 ring-offset-1";

/**
 * One descriptor rendered as an input.
 *
 * Values are held as strings (or booleans) while editing and converted once on
 * submit — a half-typed number or date should not be coerced mid-keystroke.
 */
export function CredentialFormFieldInput({
  field,
  value,
  onChange,
  disabled = false,
  changedFrom,
  onRevert,
  appearance = "default",
}: {
  field: FieldDescriptor;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  /** Identity fields are locked while editing — see `isIdentity`. */
  disabled?: boolean;
  /**
   * What this field says on the record, when the form no longer agrees with it.
   *
   * Supplied only while editing something that exists: a parse can fill half a
   * form with values nobody has checked, and the reader has to be able to see
   * WHICH values it changed and what they were before, without opening the
   * document. Undefined means "nothing to compare with" — creating, or a field
   * still matching the record — and the input renders plainly.
   */
  changedFrom?: string | boolean;
  onRevert?: () => void;
  appearance?: "default" | "profile";
}) {
  const id = `${field.source}-${field.key}`;
  const isChanged = changedFrom !== undefined;
  const isProfile = appearance === "profile";
  const controlClassName = cn(
    isProfile && PROFILE_FORM_STYLES.control,
    isChanged && CHANGED_RING,
  );
  const selectContentClassName = cn(
    isProfile &&
      "rounded-xl border-slate-200 bg-white p-1 text-slate-700 shadow-lg",
  );
  const selectItemClassName = cn(
    isProfile &&
      "rounded-lg py-2.5 text-sm focus:bg-blue-50 focus:text-blue-700",
  );

  if (field.kind === "boolean") {
    return (
      <div
        className={cn(
          "flex items-center gap-2 py-1",
          isProfile &&
            "gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5",
        )}
      >
        <Checkbox
          id={id}
          checked={value === true}
          disabled={disabled}
          className={cn(
            isProfile &&
              "size-5 rounded-md border-slate-300 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 focus-visible:ring-blue-500",
            isChanged && CHANGED_RING,
          )}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <Label
          htmlFor={id}
          className={cn(
            "text-sm font-normal",
            isProfile && PROFILE_FORM_STYLES.label,
          )}
        >
          {field.label}
        </Label>
        {isChanged && (
          <ChangedFromNote
            previous={changedFrom}
            disabled={disabled}
            onRevert={onRevert}
          />
        )}
      </div>
    );
  }

  const textValue = typeof value === "string" ? value : "";

  return (
    <div className={cn("space-y-1.5", isProfile && "min-w-0 space-y-2")}>
      <Label
        htmlFor={id}
        className={cn("text-sm", isProfile && PROFILE_FORM_STYLES.label)}
      >
        {field.label}
        {field.required && <span className="ml-0.5 text-red-600">*</span>}
      </Label>

      {field.kind === "select" && (
        <Select
          value={textValue}
          disabled={disabled}
          onValueChange={(next) => onChange(next)}
        >
          <SelectTrigger id={id} className={controlClassName}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent className={selectContentClassName}>
            {field.options?.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className={selectItemClassName}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {field.kind === "state" && (
        <Select
          value={textValue}
          disabled={disabled}
          onValueChange={(next) => onChange(next)}
        >
          <SelectTrigger id={id} className={controlClassName}>
            <SelectValue placeholder="Select a state…" />
          </SelectTrigger>
          <SelectContent className={selectContentClassName}>
            {STATES.map(([name, code]) => (
              <SelectItem
                key={code}
                value={code}
                className={selectItemClassName}
              >
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {field.kind === "textarea" && (
        <Textarea
          id={id}
          value={textValue}
          disabled={disabled}
          className={cn(
            isProfile && PROFILE_FORM_STYLES.textarea,
            isChanged && CHANGED_RING,
          )}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {(field.kind === "text" ||
        field.kind === "date" ||
        field.kind === "number" ||
        field.kind === "string-list") && (
        <Input
          id={id}
          type={
            field.kind === "date"
              ? "date"
              : field.kind === "number"
                ? "number"
                : "text"
          }
          value={textValue}
          disabled={disabled}
          className={controlClassName}
          placeholder={
            field.kind === "string-list" ? "Comma separated" : undefined
          }
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {isChanged && (
        <ChangedFromNote
          previous={changedFrom}
          disabled={disabled}
          onRevert={onRevert}
        />
      )}
      {field.helpText && (
        <p
          className={cn(
            "text-xs text-gray-500",
            isProfile && "leading-relaxed text-slate-500",
          )}
        >
          {field.helpText}
        </p>
      )}
      {disabled && field.isIdentity && (
        <p
          className={cn(
            "text-xs text-gray-500",
            isProfile && "leading-relaxed text-slate-500",
          )}
        >
          Identifies this credential and cannot be changed. Add a new credential
          and remove this one instead.
        </p>
      )}
    </div>
  );
}

/**
 * What the record says, and a way back to it.
 *
 * Both halves matter: a highlighted field with no previous value tells someone
 * that something changed but not what they would be losing, and knowing that
 * without being able to undo it makes them retype it from the document. The
 * value stays editable throughout — a parse is a suggestion, and a misread date
 * is corrected here rather than by reverting and starting again.
 */
function ChangedFromNote({
  previous,
  disabled,
  onRevert,
}: {
  previous: string | boolean;
  disabled?: boolean;
  onRevert?: () => void;
}) {
  const wasEmpty = previous === "" || previous === false;
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-xs text-amber-700">
      <span>
        {wasEmpty
          ? "Was empty"
          : `Was: ${previous === true ? "Yes" : previous}`}
      </span>
      {onRevert && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-xs font-normal text-amber-800 hover:bg-amber-50 hover:text-amber-900"
          onClick={onRevert}
        >
          <Undo2 className="h-3 w-3" aria-hidden="true" />
          Revert
        </Button>
      )}
    </p>
  );
}
